<template>
  <div class="container full-height">
    <div class="row justify-content-center align-items-center full-height">
      <div class="col-md-6 col-sm container">

        <template v-if="isReady">
          <div class="row">
            <div class="col mt-3" :style="{ fontSize: FontSizes.large2 }">
              パスワードリセット
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-4">新しいパスワード</div>
            <PasswordInput class="col-8" v-model:password="password" />
          </div>
          <div class="row mt-3">
            <div class="col-4">パスワード確認</div>
            <PasswordInput class="col-8" v-model:password="confirmPassword" />
          </div>
          <div class="row justify-content-end mt-3">
            <button
              class="col-8 btn btn-primary"
              :disabled="!canSubmit()"
              @click="onClickSubmit"
            >
              リセット
            </button>
          </div>
        </template>

        <div class="row mt-3">
          <div class="col warning-message text-center">
            {{ warningMessage }}
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Success Modal -->
  <div v-if="showSuccessModal" class="success-overlay">
    <div class="success-modal">
      <h5 class="mb-2">パスワードを更新しました</h5>
      <p class="mb-1">新しいパスワードでログインしてください。</p>
      <p class="countdown-text">{{ countdown }}秒後に自動でログイン画面へ移動します</p>
      <button class="btn btn-primary w-100" @click="goToLogin">
        ログイン画面へ
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, warn } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PasswordInput from '@/components/PasswordInput.vue'
import { FontSizes } from '@/utils/font-sizes'
import { loadingOverlayStateStore } from '@/stores/loading-overlay-state-store'
import { backendApi } from '@/utils/backend-api'
import { subPaths } from '@/utils/consts'

export default defineComponent({
  name: 'ResetPassword',
  components: { PasswordInput },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const isReady = ref<boolean>(false)
    const staffId = ref<string>('')
    const sessionToken = ref<string>('')
    const password = ref<string>('')
    const confirmPassword = ref<string>('')
    const warningMessage = ref<string>('')
    const showSuccessModal = ref<boolean>(false)
    const countdown = ref<number>(5)
    let countdownTimer: ReturnType<typeof setInterval> | null = null

    const canSubmit = (): boolean => {
      return password.value.length >= 12 && confirmPassword.value.length >= 12
    }

    const goToLogin = () => {
      if (countdownTimer) clearInterval(countdownTimer)
      router.push(subPaths.LOGIN)
    }

    const startCountdown = () => {
      showSuccessModal.value = true
      countdownTimer = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          goToLogin()
        }
      }, 1000)
    }

    const onClickSubmit = async () => {
      if (password.value !== confirmPassword.value) {
        warningMessage.value = '● 新しいパスワードが一致しません'
        return
      }
      warningMessage.value = ''
      const res = await backendApi.inquireUpdatePasswordWithToken(staffId.value, password.value, sessionToken.value)
      if (res.isSuccessful) {
        startCountdown()
      } else if(password.value.length < 12 && confirmPassword.value.length < 12) {
        warningMessage.value = 'パスワードは12文字以上でご設定をお願いいたします。'
      } else {
        // バックエンドが具体的な失敗理由（例：漏洩パスワード検知）を description で返すので、
        // それをそのまま表示する。空/未設定のときのみ汎用フォールバックを使う。
        warningMessage.value = res.description
          ? `● ${res.description}`
          : '● パスワードの更新に失敗しました。'
      }
    }

    onMounted(async () => {
      const rawToken = route.query.token as string

      router.replace({ path: subPaths.RESET_PASSWORD })

      if (!rawToken) {
        warningMessage.value = '● リンクが無効です。'
        loadingOverlayStateStore.commit('fadeout')
        return
      }

      const result = await backendApi.verifyResetToken(rawToken)
      if (result) {
        staffId.value = result.userId
        sessionToken.value = result.sessionToken
        isReady.value = true
      } else {
        warningMessage.value = '● リンクが無効または期限切れです。'
      }

      loadingOverlayStateStore.commit('fadeout')
    })

    onUnmounted(() => {
      if (countdownTimer) clearInterval(countdownTimer)
    })

    return {
      isReady,
      password,
      confirmPassword,
      warningMessage,
      showSuccessModal,
      countdown,
      canSubmit,
      onClickSubmit,
      goToLogin,
      FontSizes,
    }
  },
})
</script>

<style scoped lang="scss">
.full-height {
  height: 70vh;
}

.warning-message {
  color: red;
  // バックエンドのdescriptionに\nが含まれる場合、改行として表示する。
  white-space: pre-line;
}

.success-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-modal {
  background: white;
  border-radius: 12px;
  padding: 32px 28px;
  width: 320px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.success-icon {
  font-size: 48px;
  color: #28a745;
  margin-bottom: 12px;
}

.countdown-text {
  font-size: 0.85em;
  color: #6c757d;
  margin-bottom: 20px;
}
</style>
